// ── FalloutAnalysisPage — analysis of Declined (Lost) opportunities ──
// All lost opportunities (is_closed && !is_won). Recreates the PowerBI "Fallout" report + extras.
import { useState, useEffect, useMemo } from "react";
import { B } from "../../../shared/theme";
import { fmt, fmtDate, exportCSV } from "../../../shared/format";
import { Btn, Card, ErrorMsg, ExportBtn, STh, SectionHeader, Spinner, Td, TdNum, Th } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";
import { OwnerMultiSelect, isExcludedOwner } from "./analyticsShared";

const amt = (x) => parseFloat(x.commitment_amount || 0);
const asDate = (v) => { if (!v) return null; const d = new Date(v); return isNaN(d) ? null : d; };
const quarterKey = (d) => d.getFullYear() + " Q" + (Math.floor(d.getMonth() / 3) + 1);
const quarterIdx = (d) => d.getFullYear() * 4 + Math.floor(d.getMonth() / 3);

export const FalloutAnalysisPage = () => {
  const { api } = useApp();
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // slicers
  const [company, setCompany] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sourceSel, setSourceSel] = useState("");
  const [ownerSel, setOwnerSel] = useState(null);   // Sourced By multi-select; null = default (all except excluded four)
  const [industrySel, setIndustrySel] = useState("");
  const [typeSel, setTypeSel] = useState("");
  const [reason, setReason] = useState("");   // click-to-filter from the reason table
  const [quarter, setQuarter] = useState(null);   // click-to-filter from the bar chart (quarter idx)

  const load = () => {
    setLoading(true);
    api.get("getOpportunities")
      .then(o => { setOpps(Array.isArray(o) ? o : []); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  // Base: only Declined (Lost) opportunities.
  const lost = useMemo(() => opps.filter(o => o.is_closed && !o.is_won), [opps]);

  const sources = useMemo(() => [...new Set(lost.map(o => o.lead_source).filter(Boolean))].sort(), [lost]);
  const owners = useMemo(() => [...new Set(lost.map(o => o.ownerName).filter(Boolean))].sort(), [lost]);
  const industries = useMemo(() => [...new Set(lost.map(o => o.account_industry).filter(Boolean))].sort(), [lost]);
  const types = useMemo(() => [...new Set(lost.map(o => o.opportunity_type).filter(Boolean))].sort(), [lost]);

  // Default Sourced By to every owner except the excluded four; unassigned (no owner) always shown.
  useEffect(() => { if (ownerSel === null && owners.length) setOwnerSel(new Set(owners.filter(o => !isExcludedOwner(o)))); }, [owners, ownerSel]);
  const ownerOk = (name) => !name ? true : (ownerSel ? ownerSel.has(name) : !isExcludedOwner(name));

  const rows = useMemo(() => {
    const c = company.trim().toLowerCase();
    const fromD = from ? new Date(from) : null;
    const toD = to ? new Date(to) : null;
    return lost.filter(o => {
      if (c && !((o.companyname || o.name || "").toLowerCase().includes(c))) return false;
      if (sourceSel && o.lead_source !== sourceSel) return false;
      if (!ownerOk(o.ownerName)) return false;
      if (industrySel && o.account_industry !== industrySel) return false;
      if (typeSel && o.opportunity_type !== typeSel) return false;
      if (reason && (o.termination_reason || "Unspecified") !== reason) return false;
      if (fromD || toD || quarter != null) {
        const d = asDate(o.terminated_date);
        if (!d) return false;
        if (fromD && d < fromD) return false;
        if (toD && d > toD) return false;
        if (quarter != null && quarterIdx(d) !== quarter) return false;
      }
      return true;
    });
  }, [lost, company, from, to, sourceSel, ownerSel, industrySel, typeSel, reason, quarter]);

  // sortable detail table
  const { sorted: detailSorted, sortKey, sortDir, toggle } = useSortable(rows, 'terminated_date', 'asc');

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  const totalCnt = rows.length;
  const totalAmt = rows.reduce((s, o) => s + amt(o), 0);
  const avg = totalCnt ? totalAmt / totalCnt : 0;
  const largest = rows.reduce((m, o) => Math.max(m, amt(o)), 0);

  // "Lost to a lender" (reason starts with "Lost") vs "Passed" (we declined).
  const lostToLender = rows.filter(o => (o.termination_reason || "").toLowerCase().startsWith("lost"));
  const passed = rows.filter(o => (o.termination_reason || "").toLowerCase().startsWith("passed"));

  // ── group helper: {key, cnt, amt} sorted by count desc, then amount desc ──
  const groupBy = (fn, label = "Unspecified") => {
    const m = new Map();
    rows.forEach(o => {
      const k = fn(o) || label;
      const e = m.get(k) || { key: k, cnt: 0, amt: 0 };
      e.cnt += 1; e.amt += amt(o);
      m.set(k, e);
    });
    return [...m.values()].sort((a, b) => b.cnt - a.cnt || b.amt - a.amt);
  };

  const byReason = groupBy(o => o.termination_reason);
  const bySource = groupBy(o => o.lead_source);
  const byIndustry = groupBy(o => o.account_industry);
  const byOwner = groupBy(o => o.ownerName);
  const byState = groupBy(o => o.account_state);

  // ── amount by year+quarter (continuous axis, terminated_date) ──
  const qMap = new Map();
  let minIdx = Infinity, maxIdx = -Infinity;
  rows.forEach(o => {
    const d = asDate(o.terminated_date);
    if (!d) return;
    const idx = quarterIdx(d);
    minIdx = Math.min(minIdx, idx); maxIdx = Math.max(maxIdx, idx);
    const e = qMap.get(idx) || { idx, label: quarterKey(d), amt: 0, cnt: 0 };
    e.amt += amt(o); e.cnt += 1;
    qMap.set(idx, e);
  });
  const quarters = [];
  if (isFinite(minIdx)) {
    for (let i = minIdx; i <= maxIdx; i++) {
      const y = Math.floor(i / 4), q = (i % 4) + 1;
      quarters.push(qMap.get(i) || { idx: i, label: y + " Q" + q, amt: 0, cnt: 0 });
    }
  }
  const qMax = Math.max(1, ...quarters.map(q => q.amt));

  const cardTitle = { fontSize: 11, fontWeight: 700, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12, paddingBottom: 6, borderBottom: "1.5px solid " + B.cyanLight };
  const stat = { fontSize: 24, fontWeight: 600, lineHeight: 1.1 };
  const statSub = { fontSize: 11, color: "var(--color-text-secondary)", marginTop: 4 };
  const selStyle = { padding: "6px 10px", fontSize: 13, border: "1px solid var(--color-border-secondary)", borderRadius: 6, background: "var(--color-background-primary)", color: "var(--color-text-primary)" };

  const Dropdown = ({ value, onChange, all, opts }) => (
    <select value={value} onChange={e => onChange(e.target.value)} style={selStyle}>
      <option value="">{all}</option>
      {opts.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  // reason table is clickable → filters the page
  const ReasonTable = () => (
    <Card style={{ padding: 16, overflowX: "auto" }}>
      <div style={cardTitle}>Termination Reason</div>
      <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap", fontSize: 12 }}>
        <thead><tr><Th>Reason</Th><Th style={{ textAlign: "right" }}>Cnt</Th><Th style={{ textAlign: "right" }}>Amount</Th></tr></thead>
        <tbody>{byReason.length === 0 ? <tr><Td colSpan={3} style={{ color: "var(--color-text-secondary)" }}>None.</Td></tr> : byReason.map(r => {
          const active = reason === r.key;
          return (
            <tr key={r.key} title="Click to filter by this reason" style={{ cursor: "pointer", background: active ? B.cyanLight : "" }}
              onClick={() => setReason(prev => prev === r.key ? "" : r.key)}
              onMouseEnter={e => { if (!active) e.currentTarget.style.background = "var(--color-background-secondary)"; }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.background = ""; }}>
              <Td style={{ fontWeight: active ? 700 : 400 }}>{r.key}</Td>
              <TdNum>{r.cnt}</TdNum>
              <TdNum>{fmt(r.amt)}</TdNum>
            </tr>
          );
        })}</tbody>
        <tfoot><tr style={{ background: "var(--color-background-secondary)" }}>
          <td style={{ padding: "8px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)" }}>Total</td>
          <td style={{ textAlign: "right", padding: "8px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{totalCnt}</td>
          <td style={{ textAlign: "right", padding: "8px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{fmt(totalAmt)}</td>
        </tr></tfoot>
      </table>
    </Card>
  );

  const MiniTable = ({ title, data, limit }) => {
    const list = limit ? data.slice(0, limit) : data;
    const tCnt = list.reduce((s, r) => s + r.cnt, 0);
    const tAmt = list.reduce((s, r) => s + r.amt, 0);
    const tdF = { padding: "8px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)" };
    const tdFR = { ...tdF, textAlign: "right", fontVariantNumeric: "tabular-nums" };
    return (
      <Card style={{ padding: 16, overflowX: "auto" }}>
        <div style={cardTitle}>{title}</div>
        <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap", fontSize: 12 }}>
          <thead><tr><Th>{title.replace(/^By |^Top /, "")}</Th><Th style={{ textAlign: "right" }}>Cnt</Th><Th style={{ textAlign: "right" }}>Amount</Th></tr></thead>
          <tbody>{list.length === 0 ? <tr><Td colSpan={3} style={{ color: "var(--color-text-secondary)" }}>None.</Td></tr> : list.map(r => (
            <tr key={r.key}><Td>{r.key}</Td><TdNum>{r.cnt}</TdNum><TdNum>{fmt(r.amt)}</TdNum></tr>
          ))}</tbody>
          <tfoot><tr style={{ background: "var(--color-background-secondary)" }}>
            <td style={tdF}>{limit && data.length > limit ? "Top " + list.length + " total" : "Total"}</td>
            <td style={tdFR}>{tCnt}</td>
            <td style={tdFR}>{fmt(tAmt)}</td>
          </tr></tfoot>
        </table>
      </Card>
    );
  };

  const detailCols = ["companyname", "terminated_date", "termination_reason", "lead_source", "account_industry", "account_state", "commitment_amount", "stage"];

  return <>
    <SectionHeader title="Fallout Analysis" actions={[
      <ExportBtn key="ex" onClick={() => exportCSV(detailSorted, detailCols, "fallout_analysis.csv")}>Export CSV</ExportBtn>
    ]} />

    {/* slicers */}
    <Card style={{ padding: 12, marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
      <input value={company} onChange={e => setCompany(e.target.value)} placeholder="Company…" style={{ ...selStyle, minWidth: 160 }} />
      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--color-text-secondary)" }}>
        Terminated
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={selStyle} />
        <span>–</span>
        <input type="date" value={to} onChange={e => setTo(e.target.value)} style={selStyle} />
      </label>
      <Dropdown value={sourceSel} onChange={setSourceSel} all="All Lead Sources" opts={sources} />
      <OwnerMultiSelect owners={owners} value={ownerSel} onChange={setOwnerSel} />
      <Dropdown value={industrySel} onChange={setIndustrySel} all="All Industries" opts={industries} />
      <Dropdown value={typeSel} onChange={setTypeSel} all="All Opp Types" opts={types} />
      {reason && (
        <button onClick={() => setReason("")} title="Clear reason filter"
          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", fontSize: 12, fontWeight: 600, border: "1px solid " + B.cyan, borderRadius: 999, background: B.cyanLight, color: B.cyanDark, cursor: "pointer" }}>
          Reason: {reason} <span style={{ fontWeight: 700 }}>✕</span>
        </button>
      )}
      {quarter != null && (
        <button onClick={() => setQuarter(null)} title="Clear quarter filter"
          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", fontSize: 12, fontWeight: 600, border: "1px solid " + B.cyan, borderRadius: 999, background: B.cyanLight, color: B.cyanDark, cursor: "pointer" }}>
          Quarter: {Math.floor(quarter / 4) + " Q" + ((quarter % 4) + 1)} <span style={{ fontWeight: 700 }}>✕</span>
        </button>
      )}
      <div style={{ marginLeft: "auto", fontSize: 12, color: "var(--color-text-secondary)" }}>
        {totalCnt} lost · {fmt(totalAmt)}
      </div>
    </Card>

    {/* KPI row */}
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
      <Card style={{ padding: 16 }}><div style={cardTitle}>Lost Deals</div><div style={stat}>{totalCnt}</div><div style={statSub}>Declined (Lost) opportunities</div></Card>
      <Card style={{ padding: 16 }}><div style={cardTitle}>Lost Amount</div><div style={stat}>{fmt(totalAmt)}</div><div style={statSub}>Total committed value lost</div></Card>
      <Card style={{ padding: 16 }}><div style={cardTitle}>Avg Lost Deal</div><div style={stat}>{fmt(avg)}</div><div style={statSub}>Largest {fmt(largest)}</div></Card>
      <Card style={{ padding: 16 }}><div style={cardTitle}>Passed vs Lost-to-Lender</div>
        <div style={{ ...stat, fontSize: 20 }}>{passed.length} · {lostToLender.length}</div>
        <div style={statSub}>{fmt(passed.reduce((s, o) => s + amt(o), 0))} passed / {fmt(lostToLender.reduce((s, o) => s + amt(o), 0))} lost</div>
      </Card>
    </div>

    {/* amount by year+quarter */}
    <Card style={{ padding: 16, marginBottom: 16 }}>
      <div style={cardTitle}>Lost Amount by Year and Quarter</div>
      {quarters.length === 0 ? <div style={statSub}>No terminated dates in range.</div> : (
        <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 220, paddingTop: 8, overflowX: "auto" }}>
          {quarters.map(q => {
            const active = quarter === q.idx;
            return (
            <div key={q.idx} title={q.label + " · " + fmt(q.amt) + " · " + q.cnt + " deals — click to filter"}
              onClick={() => setQuarter(prev => prev === q.idx ? null : q.idx)}
              style={{ flex: "1 0 44px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%", minWidth: 44, cursor: "pointer" }}>
              <div style={{ fontSize: 10, color: "var(--color-text-secondary)", marginBottom: 3, whiteSpace: "nowrap" }}>{q.amt > 0 ? fmt(q.amt) : ""}</div>
              <div style={{ width: "70%", maxWidth: 34, height: Math.max(1, (q.amt / qMax) * 160) + "px", background: active ? B.amber : B.cyan, borderRadius: "4px 4px 0 0" }} />
              <div style={{ fontSize: 9, color: active ? B.cyanDark : "var(--color-text-secondary)", fontWeight: active ? 700 : 400, marginTop: 6, textAlign: "center", lineHeight: 1.2, whiteSpace: "nowrap" }}>{q.label}</div>
            </div>
            );
          })}
        </div>
      )}
    </Card>

    {/* reason + lead source */}
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
      <ReasonTable />
      <MiniTable title="By Lead Source" data={bySource} />
    </div>

    {/* industry + owner + state */}
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
      <MiniTable title="By Industry" data={byIndustry} />
      <MiniTable title="By Owner" data={byOwner} />
      <MiniTable title="Top States" data={byState} limit={10} />
    </div>

    {/* detail table */}
    <Card style={{ overflowX: "auto" }}>
      <div style={{ ...cardTitle, padding: "12px 16px 6px", marginBottom: 0 }}>Lost Opportunity Detail</div>
      <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap", fontSize: 12 }}>
        <thead><tr>
          <STh label="Opportunity Name"  sortKey="companyname"        currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Terminated"        sortKey="terminated_date"    currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Termination Reason" sortKey="termination_reason" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Lead Source"       sortKey="lead_source"        currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Industry"          sortKey="account_industry"   currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="State"             sortKey="account_state"      currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Amount"            sortKey="commitment_amount"  currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
          <STh label="Stage"             sortKey="stage"              currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
        </tr></thead>
        <tbody>{detailSorted.length === 0 ? <tr><Td colSpan={8} style={{ color: "var(--color-text-secondary)" }}>No lost opportunities.</Td></tr> : detailSorted.map(o => (
          <tr key={o.id} onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={e => e.currentTarget.style.background = ""}>
            <Td style={{ fontWeight: 500 }}>{o.companyname || o.name || "—"}</Td>
            <Td>{fmtDate(o.terminated_date)}</Td>
            <Td>{o.termination_reason || "—"}</Td>
            <Td>{o.lead_source || "—"}</Td>
            <Td>{o.account_industry || "—"}</Td>
            <Td>{o.account_state || "—"}</Td>
            <TdNum>{fmt(amt(o))}</TdNum>
            <Td>{o.stage || "—"}</Td>
          </tr>
        ))}</tbody>
        <tfoot><tr style={{ background: "var(--color-background-secondary)" }}>
          <td colSpan={6} style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)" }}>Total ({detailSorted.length})</td>
          <td style={{ textAlign: "right", padding: "10px 12px", fontSize: 13, fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{fmt(detailSorted.reduce((s, o) => s + amt(o), 0))}</td>
          <td style={{ borderTop: "1.5px solid var(--color-border-secondary)" }} />
        </tr></tfoot>
      </table>
    </Card>
  </>;
};
